/**
 * lib/analytics.js — Centralised tracking helper for GA4 + Meta Pixel.
 *
 * Design principles:
 *   1. Scripts load lazily and ONLY when: (a) a valid ID is present,
 *      (b) tracking is enabled by the admin, and (c) the visitor has
 *      given consent (or consent is not required).
 *   2. Every event goes through `trackEvent()` — that helper decides
 *      which providers to hit based on the current config and role.
 *   3. Staff (admin/moderator) sessions are excluded by default so
 *      internal QA / review activity never pollutes marketing data.
 *   4. All calls are safe to invoke before scripts have loaded — the
 *      helpers queue via the standard `dataLayer` / `_fbq` shims.
 *
 * Public API used by the rest of the app:
 *   * initAnalytics({ ga4, pixel, excludeStaff, isStaff })
 *   * disableAnalytics() — no-op if not initialised
 *   * trackEvent(name, params?) — fires to whichever providers loaded
 *   * trackPageview(url?, title?) — same, for SPA route changes
 *
 * Consent handling lives in `AnalyticsProvider.jsx` — this file only
 * cares about "has init been called with valid IDs".
 */

const STATE = {
  ga4: '',
  pixel: '',
  ga4Loaded: false,
  pixelLoaded: false,
  suppressed: false, // true when staff-exclusion trips or admin killed tracking
  ready: false,
};

// Silent no-op in server-render / test environments
const _win = () => (typeof window !== 'undefined' ? window : null);

const injectScript = (src, id) => {
  const w = _win();
  if (!w || w.document.getElementById(id)) return;
  const s = w.document.createElement('script');
  s.async = true;
  s.src = src;
  s.id = id;
  w.document.head.appendChild(s);
};

const loadGA4 = (measurementId) => {
  const w = _win();
  if (!w || STATE.ga4Loaded || !measurementId) return;
  // gtag boilerplate
  w.dataLayer = w.dataLayer || [];
  w.gtag = function gtag() { w.dataLayer.push(arguments); };
  w.gtag('js', new Date());
  // send_page_view=false so the SPA can emit page_view on route change instead
  w.gtag('config', measurementId, { send_page_view: false });
  injectScript(`https://www.googletagmanager.com/gtag/js?id=${measurementId}`, 'ga4-src');
  STATE.ga4Loaded = true;
};

const loadMetaPixel = (pixelId) => {
  const w = _win();
  if (!w || STATE.pixelLoaded || !pixelId) return;
  // Standard fbq bootstrap (per Meta docs)
  !function(f,b,e,v,n,t,s){
    if(f.fbq) return; n=f.fbq=function(){ n.callMethod ? n.callMethod.apply(n,arguments) : n.queue.push(arguments); };
    if(!f._fbq) f._fbq=n; n.push=n; n.loaded=!0; n.version='2.0'; n.queue=[];
    t=b.createElement(e); t.async=!0; t.src=v; t.id='meta-pixel-src';
    s=b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t,s);
  }(w, w.document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  w.fbq('init', pixelId);
  STATE.pixelLoaded = true;
};

/**
 * Initialise providers. Safe to call multiple times — repeated calls
 * with the same IDs are no-ops; changes to IDs will attempt to load
 * whichever provider became newly configured (rare, but supported for
 * admin-edit flows without a page reload).
 */
export const initAnalytics = ({ ga4, pixel, excludeStaff, isStaff, trackingEnabled }) => {
  STATE.ready = true;
  // Suppression conditions
  if (!trackingEnabled) { STATE.suppressed = true; return; }
  if (excludeStaff && isStaff) { STATE.suppressed = true; return; }
  STATE.suppressed = false;
  STATE.ga4 = ga4 || '';
  STATE.pixel = pixel || '';
  if (ga4) loadGA4(ga4);
  if (pixel) loadMetaPixel(pixel);
};

export const disableAnalytics = () => {
  STATE.suppressed = true;
};

export const isAnalyticsActive = () => STATE.ready && !STATE.suppressed && (STATE.ga4Loaded || STATE.pixelLoaded);

/**
 * Fire an event to whichever providers are loaded. `params` is passed
 * through to GA4 as-is and mapped to Meta Pixel's standard payload
 * shape (currency, value, content_ids, etc.) when relevant.
 *
 * Meta Pixel has a small set of "Standard Events" that get better ad
 * optimisation — we map our custom names to those where possible; the
 * rest fall through to `trackCustom`.
 */
const META_STANDARD_MAP = {
  sign_up: 'CompleteRegistration',
  login: 'Login', // custom — Meta doesn't have a standard for this
  listing_view: 'ViewContent',
  contact_reveal: 'Contact',
  inquiry_sent: 'Lead',
  search: 'Search',
  plan_page_view: 'ViewContent',
  checkout_started: 'InitiateCheckout',
  payment_success: 'Purchase',
};

export const trackEvent = (name, params = {}) => {
  const w = _win();
  if (!w || STATE.suppressed || !name) return;
  // GA4
  if (STATE.ga4Loaded && typeof w.gtag === 'function') {
    try { w.gtag('event', name, params); } catch { /* ignore */ }
  }
  // Meta Pixel — use Standard event when we know the mapping, else custom
  if (STATE.pixelLoaded && typeof w.fbq === 'function') {
    const std = META_STANDARD_MAP[name];
    try {
      if (std && std !== 'Login') {
        w.fbq('track', std, params);
      } else {
        w.fbq('trackCustom', name, params);
      }
    } catch { /* ignore */ }
  }
};

/**
 * SPA page-view. `url` defaults to current location.pathname+search.
 */
export const trackPageview = (url, title) => {
  const w = _win();
  if (!w || STATE.suppressed) return;
  const path = url || (w.location.pathname + w.location.search);
  const docTitle = title || w.document.title;
  if (STATE.ga4Loaded && typeof w.gtag === 'function') {
    try {
      w.gtag('event', 'page_view', {
        page_location: w.location.origin + path,
        page_path: path,
        page_title: docTitle,
      });
    } catch { /* ignore */ }
  }
  if (STATE.pixelLoaded && typeof w.fbq === 'function') {
    try { w.fbq('track', 'PageView'); } catch { /* ignore */ }
  }
};

// Consent helpers — the ConsentBanner drives these via localStorage.
export const CONSENT_KEY = 'indusfact_analytics_consent'; // 'granted' | 'denied' | null

export const getConsent = () => {
  const w = _win();
  if (!w) return null;
  try { return w.localStorage.getItem(CONSENT_KEY); } catch { return null; }
};

export const setConsent = (value) => {
  const w = _win();
  if (!w) return;
  try { w.localStorage.setItem(CONSENT_KEY, value); } catch { /* ignore */ }
};
